import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import SignaturePad from 'signature_pad';
import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useTranslation } from '../../../contexts/translation-context';
import { dataUrlToWixFile } from '../utils/data-url';
import { resizeCanvas } from '../utils/canvas';
import { decodePoints, encodePoints } from '../utils/points';
import styles from './signature-drawer.module.scss';
export const SignatureDrawer = props => {
  const {
    className,
    onChange,
    value,
    theme,
    penColor,
    inputId,
    onBeforeChange,
    fieldId,
    onBlur,
    onFocus,
    defaultValue,
    hidden
  } = props;
  const pad = React.useRef();
  const canvasRef = React.useRef(null);
  const valueUrl = value == null ? void 0 : value.url;
  const {
    t
  } = useTranslation();
  const [ariaLabel, setAriaLabel] = useState(t('field.signature.canvas.aria-label.empty'));
  useFocusFieldEvent(() => {
    var _canvasRef$current;
    (_canvasRef$current = canvasRef.current) == null || _canvasRef$current.focus();
  }, fieldId);
  const options = useMemo(() => ({
    throttle: 0,
    dotSize: 2,
    minWidth: 1,
    maxWidth: 3,
    velocityFilterWeight: 0.2,
    penColor
  }), [penColor]);
  const getSignature = useCallback(() => {
    var _pad$current, _pad$current2, _pad$current3;
    if ((_pad$current = pad.current) != null && _pad$current.isEmpty()) {
      return '';
    }
    const dataPoints = (_pad$current2 = pad.current) == null ? void 0 : _pad$current2.toData();
    return dataPoints ? encodePoints(dataPoints) : ((_pad$current3 = pad.current) == null ? void 0 : _pad$current3.toDataURL()) ?? '';
  }, []);
  const handleChange = useCallback(() => {
    var _pad$current4;
    const isEmpty = (_pad$current4 = pad.current) == null ? void 0 : _pad$current4.isEmpty();
    setAriaLabel(isEmpty ? t('field.signature.canvas.aria-label.empty') : t('field.signature.canvas.aria-label.signed'));
    onChange == null || onChange(dataUrlToWixFile(getSignature()));
  }, [getSignature, onChange, t]);
  const initCanvas = useCallback(() => {
    resizeCanvas(canvasRef.current);
    if (valueUrl) {
      const points = decodePoints(valueUrl);
      if (points) {
        var _pad$current5;
        (_pad$current5 = pad.current) == null || _pad$current5.fromData(points);
        setAriaLabel(t('field.signature.canvas.aria-label.signed'));
      }
    } else {
      var _pad$current6;
      (_pad$current6 = pad.current) == null || _pad$current6.clear();
      setAriaLabel(t('field.signature.canvas.aria-label.empty'));
    }
  }, [valueUrl, t]);
  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    pad.current = new SignaturePad(canvasRef.current, options);
  }, [options]);
  useEffect(() => {
    if (!canvasRef.current || !pad.current) {
      return;
    }
    initCanvas();
    pad.current.addEventListener('endStroke', handleChange);
    window.addEventListener('resize', initCanvas);
    return function () {
      var _pad$current7;
      window.removeEventListener('resize', initCanvas);
      (_pad$current7 = pad.current) == null || _pad$current7.removeEventListener('endStroke', handleChange);
    };
  }, [handleChange, initCanvas]);
  const handleBeforeChange = useCallback(() => {
    onBeforeChange == null || onBeforeChange();
  }, [onBeforeChange]);
  useEffect(() => {
    if (!pad.current) {
      return;
    }
    if (onBeforeChange) {
      pad.current.addEventListener('beginStroke', handleBeforeChange);
    }
    return () => {
      if (handleBeforeChange) {
        window.removeEventListener('beginStroke', handleBeforeChange);
      }
    };
  }, [handleBeforeChange, onBeforeChange]);
  useFocusFieldEvent(() => {
    var _canvasRef$current2;
    (_canvasRef$current2 = canvasRef.current) == null || _canvasRef$current2.focus();
  }, fieldId);
  return /*#__PURE__*/React.createElement("div", {
    className: className,
    "data-theme": theme
  }, /*#__PURE__*/React.createElement("canvas", {
    hidden: hidden,
    defaultValue: defaultValue,
    onFocus: onFocus,
    onBlur: onBlur,
    ref: canvasRef,
    id: inputId,
    "data-input-id": inputId,
    "data-has-value": !!valueUrl,
    className: classNames(styles.canvas),
    "data-hook": "signature-canvas",
    role: "img",
    "aria-label": ariaLabel
  }));
};
//# sourceMappingURL=signature-drawer.js.map